"use client";

import Image from "next/image";
import Link from "next/link";
import { useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { Menu, ShoppingBag, X } from "lucide-react";
import { Show, SignInButton, UserButton } from "@clerk/nextjs";
import { useCartItems, useTotalItems } from "@/lib/store/cart-store-provider";
import { normalizeLocale, withLang } from "@/lib/i18n";

const NAV_COPY = {
  "en-US": {
    home: "Home", shop: "Shop", about: "About", contact: "Contact",
    checkout: "Checkout", login: "Log in", profile: "Profile",
    openMenu: "Open navigation", closeMenu: "Close navigation",
    skipToContent: "Skip to main content",
    langEN: "Switch to English", langPT: "Mudar para Português",
    cartLabel: (n: number) =>
      n > 0 ? `Checkout — ${n} item${n > 1 ? "s" : ""}` : "Checkout",
  },
  "pt-PT": {
    home: "Início", shop: "Loja", about: "Sobre", contact: "Contacto",
    checkout: "Finalizar", login: "Entrar", profile: "Perfil",
    openMenu: "Abrir menu", closeMenu: "Fechar menu",
    skipToContent: "Ir para o conteúdo",
    langEN: "Switch to English", langPT: "Mudar para Português",
    cartLabel: (n: number) =>
      n > 0 ? `Finalizar — ${n} item${n > 1 ? "s" : ""}` : "Finalizar",
  },
} as const;

/* Shared focus-ring utility – 2 px warm outline on every interactive element */
const ring =
  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2B2824]";

export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const pathname    = usePathname();
  const searchParams = useSearchParams();
  const locale      = normalizeLocale(searchParams.get("lang"));
  const items       = useCartItems();
  const totalItems  = useTotalItems();
  const t           = NAV_COPY[locale];

  const navLinks = [
    { href: "/",        label: t.home    },
    { href: "/shop",    label: t.shop    },
    { href: "/about",   label: t.about   },
    { href: "/contact", label: t.contact },
  ];

  const active = (href: string) =>
    href === "/" ? pathname === "/" : pathname.startsWith(href);

  return (
    <>
      {/* ── Skip link (WCAG 2.4.1) ── */}
      <a
        href="#main-content"
        className={`sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100]
          focus:bg-[#4F4A43] focus:px-4 focus:py-2.5
          focus:text-[10px] focus:font-bold focus:tracking-[0.20em] focus:text-[#F8F6F1] focus:uppercase
          ${ring}`}
      >
        {t.skipToContent}
      </a>

      <header className="sticky top-0 z-40 bg-[#FDFCF9]">

        {/* ══════════════════════════════════════════
            Row 1 — language · logo · actions
            Uses grid-cols-[1fr_auto_1fr] so the logo
            always stays centred without absolute hacks
        ══════════════════════════════════════════ */}
        <div className="content-wrap">
          <div className="grid h-20 grid-cols-[1fr_auto_1fr] items-center">

            {/* ── Left: language toggle ── */}
            <div
              className="flex items-center"
              role="group"
              aria-label="Language selector"
            >
              {(["en-US", "pt-PT"] as const).map((lang, i) => (
                <div key={lang} className="flex items-center">
                  {i > 0 && (
                    <span
                      className="mx-1 select-none text-[#D9D1C6]"
                      aria-hidden="true"
                    >
                      |
                    </span>
                  )}
                  <Link
                    href={withLang(pathname || "/", lang)}
                    aria-label={lang === "en-US" ? t.langEN : t.langPT}
                    aria-current={locale === lang ? "true" : undefined}
                    className={`flex h-11 min-w-[44px] items-center justify-center
                      text-[10px] font-bold tracking-[0.20em] uppercase
                      transition-colors duration-150 ${ring}
                      ${locale === lang
                        ? "text-[#2B2824]"
                        : "text-[#C0B8AD] hover:text-[#666058]"}`}
                  >
                    {lang === "en-US" ? "EN" : "PT"}
                  </Link>
                </div>
              ))}
            </div>

            {/* ── Centre: logo ── */}
            <Link
              href={withLang("/", locale)}
              aria-label="Linha de Luz — página inicial"
              className={`block py-2 ${ring} focus-visible:outline-offset-4`}
            >
              <Image
                src="/logo-alt-dark.svg"
                alt="Linha de Luz"
                width={175}
                height={62}
                priority
              />
            </Link>

            {/* ── Right: cart · auth · hamburger ── */}
            <div className="flex items-center justify-end gap-0.5">

              {/* Cart icon */}
              <Link
                href={withLang("/checkout", locale)}
                aria-label={t.cartLabel(totalItems)}
                className={`relative flex h-11 w-11 items-center justify-center
                  text-[#4F4A43] transition-colors duration-150
                  hover:text-[#2B2824] ${ring}`}
              >
                <ShoppingBag
                  className="h-[19px] w-[19px]"
                  strokeWidth={1.5}
                  aria-hidden="true"
                />
                {items.length > 0 && (
                  <span
                    aria-hidden="true"
                    className="absolute right-1 top-1 flex h-4 w-4 items-center justify-center
                      rounded-full bg-[#4F4A43] text-[8px] font-black leading-none text-[#F8F6F1]"
                  >
                    {totalItems > 9 ? "9+" : totalItems}
                  </span>
                )}
              </Link>

              {/* Auth — desktop only */}
              <Show when="signed-out">
                <SignInButton mode="modal">
                  <button
                    className={`hidden h-11 items-center px-3
                      text-[10px] font-bold tracking-[0.18em] text-[#C0B8AD] uppercase
                      transition-colors duration-150 hover:text-[#2B2824] md:flex ${ring}`}
                  >
                    {t.login}
                  </button>
                </SignInButton>
              </Show>
              <Show when="signed-in">
                <div className="hidden h-11 items-center px-2 md:flex">
                  <UserButton />
                </div>
              </Show>

              {/* Hamburger — mobile only */}
              <button
                onClick={() => setOpen((p) => !p)}
                aria-label={open ? t.closeMenu : t.openMenu}
                aria-expanded={open}
                aria-controls="mobile-nav"
                className={`flex h-11 w-11 items-center justify-center
                  text-[#4F4A43] transition-colors duration-150
                  hover:text-[#2B2824] md:hidden ${ring}`}
              >
                <span
                  aria-hidden="true"
                  className="transition-transform duration-200 ease-out"
                  style={{ display: "block", transform: open ? "rotate(90deg)" : "none" }}
                >
                  {open
                    ? <X className="h-5 w-5" strokeWidth={1.5} />
                    : <Menu className="h-5 w-5" strokeWidth={1.5} />}
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════════
            Row 2 — desktop nav
            li-level border-b + items-stretch keeps the
            active underline pixel-perfect at row bottom
        ══════════════════════════════════════════ */}
        <div className=" border-b border-t border-[#ECEAE5]">
          <nav aria-label="Primary navigation" className="hidden md:block">
            <ul
              role="list"
              className="content-wrap flex h-[46px] mb-3 items-stretch justify-center gap-8"
            >
              {navLinks.map(({ href, label }) => (
                <li
                  key={href}
                  className={`flex items-center border-b-[1.5px] transition-colors duration-150 ${
                    active(href) ? "border-[#2B2824]" : "border-transparent"
                  }`}
                >
                  <Link
                    href={withLang(href, locale)}
                    aria-current={active(href) ? "page" : undefined}
                    className={`text-[11px] font-bold tracking-[0.25em] uppercase
                      transition-colors duration-150 ${ring} focus-visible:outline-offset-4
                      ${active(href)
                        ? "text-[#2B2824]"
                        : "text-[#A09589] hover:text-[#2B2824]"}`}
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>

        {/* ══════════════════════════════════════════
            Mobile drawer
            grid-rows 0fr → 1fr avoids max-height jank
        ══════════════════════════════════════════ */}
        <div
          id="mobile-nav"
          aria-hidden={!open}
          className={`grid border-t border-[#ECEAE5] bg-[#FDFCF9]
            transition-[grid-template-rows] duration-200 ease-out md:hidden
            ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
        >
          <div className="overflow-hidden">
            <nav aria-label="Mobile navigation">
              <ul
                role="list"
                className="divide-y divide-[#ECEAE5] px-5 pb-5 pt-1"
              >
                {/* Nav pages */}
                {navLinks.map(({ href, label }) => (
                  <li key={href}>
                    <Link
                      href={withLang(href, locale)}
                      onClick={() => setOpen(false)}
                      aria-current={active(href) ? "page" : undefined}
                      className={`flex h-14 items-center gap-3 text-[11px] font-bold
                        tracking-[0.22em] uppercase transition-colors duration-150 ${ring}
                        ${active(href)
                          ? "text-[#2B2824]"
                          : "text-[#8E7F6D] hover:text-[#2B2824]"}`}
                    >
                      {active(href) && (
                        <span
                          className="h-1 w-1 rounded-full bg-[#4F4A43]"
                          aria-hidden="true"
                        />
                      )}
                      {label}
                    </Link>
                  </li>
                ))}

                {/* Checkout */}
                <li>
                  <Link
                    href={withLang("/checkout", locale)}
                    onClick={() => setOpen(false)}
                    className={`flex h-14 items-center justify-between
                      text-[11px] font-bold tracking-[0.22em] text-[#8E7F6D] uppercase
                      transition-colors hover:text-[#2B2824] ${ring}`}
                  >
                    {t.checkout}
                    {totalItems > 0 && (
                      <span
                        className="flex h-5 w-5 items-center justify-center
                          rounded-full bg-[#4F4A43] text-[9px] font-black text-[#F8F6F1]"
                      >
                        {totalItems > 9 ? "9+" : totalItems}
                      </span>
                    )}
                  </Link>
                </li>

                {/* Auth */}
                <li className="flex h-14 items-center">
                  <Show when="signed-out">
                    <SignInButton mode="modal">
                      <button
                        className={`text-[11px] font-bold tracking-[0.22em]
                          text-[#C0B8AD] uppercase transition-colors hover:text-[#2B2824] ${ring}`}
                      >
                        {t.login}
                      </button>
                    </SignInButton>
                  </Show>
                  <Show when="signed-in">
                    <div className="flex items-center gap-3">
                      <span className="text-[11px] font-bold tracking-[0.22em] text-[#8E7F6D] uppercase">
                        {t.profile}
                      </span>
                      <UserButton />
                    </div>
                  </Show>
                </li>
              </ul>
            </nav>
          </div>
        </div>

      </header>
    </>
  );
}
